'use client'

import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { MeseroFrame } from '@/modules/mesero/components/MeseroFrame'
import { useMesero } from '@/modules/mesero/MeseroProvider'
import { cn, toMs } from '@/lib/utils'
import type { Table } from '@/lib/pos-types'

export default function AlertasPage() {
  const navigate = useNavigate()
  const { currentUser, tables, orders, activityLogs, markAlertsRead } = useMesero()

  const readAt = useMemo(() => {
    if (typeof window === 'undefined') return 0
    const key = `mfoodx_mesero_alerts_read_at_v1:${currentUser.id ?? currentUser.name}`
    const raw = window.localStorage.getItem(key)
    const parsed = raw ? Date.parse(raw) : 0
    return Number.isFinite(parsed) ? parsed : 0
  }, [currentUser.id, currentUser.name])

  const alerts = useMemo(() => {
    return activityLogs
      .filter((a) => (a.action === 'order_ready' || a.action === 'order_adjusted') && Boolean(a.tableNumber))
      .map((a) => {
        let table: Table | undefined
        if (a.entityType === 'order' && a.entityId) {
          const order = orders.find((o) => o.id === a.entityId)
          if (order && order.tableId) {
            table = tables.find((t) => t.id === order.tableId)
          }
        }
        if (!table && a.tableNumber) {
          table = tables.find((t) => t.number === a.tableNumber && t.waiter === currentUser.name)
          if (!table) {
            table = tables.find((t) => t.number === a.tableNumber)
          }
        }
        return { log: a, table }
      })
      .filter(({ table }) => table?.waiter === currentUser.name)
      .slice()
      .sort((a, b) => toMs(b.log.timestamp) - toMs(a.log.timestamp))
  }, [activityLogs, currentUser.name, tables, orders])

  useEffect(() => {
    markAlertsRead()
  }, [markAlertsRead])

  const handleTap = (tableId: string | undefined) => {
    if (!tableId) return
    navigate(`/mesero/mesa/${tableId}/items`)
  }

  return (
    <MeseroFrame>
      <div className="flex-1 overflow-hidden flex flex-col bg-[#0f0f11]">
        <div className="px-5 pt-3 pb-2 flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => navigate('/mesero/mesas')}
            className="w-9 h-9 rounded-full bg-[#1e1e23] border border-white/10 flex items-center justify-center"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#f0f0f0" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div className="text-[18px] font-semibold text-[#f0f0f0] leading-tight">Alertas</div>
        </div>

        <div className="flex-1 overflow-y-auto px-5">
          {alerts.length === 0 ? (
            <div className="py-10 text-center text-[#9a9aa8]">
              <div className="text-sm font-medium text-[#f0f0f0]">Sin alertas</div>
              <div className="text-xs mt-1">Aquí aparecen mesas listas para servir</div>
            </div>
          ) : (
            <div className="divide-y divide-white/10">
              {alerts.map(({ log: a, table }) => {
                const unread = toMs(a.timestamp) > readAt
                const tableDesc = table ? `Mesa ${table.number} (${table.zone})` : `Mesa ${a.tableNumber}`
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => handleTap(table?.id)}
                    className="w-full text-left py-4 flex gap-3"
                  >
                    <div className={cn('w-2 h-2 rounded-full mt-1.5 shrink-0', unread ? 'bg-[#00e5a0]' : 'bg-[#5a5a6a] opacity-40')} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-[#f0f0f0] truncate">
                        {a.action === 'order_adjusted' ? `${tableDesc} ajustada` : `${tableDesc} lista`}
                      </div>
                      <div className="text-[12px] text-[#9a9aa8] mt-0.5 leading-snug">{a.details}</div>
                    </div>
                    <div className="text-[11px] text-[#5a5a6a] font-mono shrink-0">
                      {formatTime(a.timestamp)}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </MeseroFrame>
  )
}

function formatTime(val: Date | string | number | null | undefined) {
  const ms = toMs(val)
  if (!ms) return ''
  const d = new Date(ms)
  try {
    return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
  } catch {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }
}
